import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, FileText, ShieldCheck } from "lucide-react";
import { Brand } from "@/components/Layout";
import { LinkHero } from "@/components/LinkHero";
import { SigningFlow } from "@/components/SigningFlow";
import { OtpGate } from "@/components/OtpGate";
import { API, api, errMsg } from "@/lib/api";

export default function RemoteSign() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/public/sign/${token}`);
      setD(r.data);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [token]);

  useEffect(() => {
    reload();
  }, [reload]);

  const sign = async (body) => {
    await api.post(`/public/sign/${token}/signatures`, body);
    await reload();
  };

  const allDone = d && d.pending.every((i) => i.missing.length === 0);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-[#D4E6EC] bg-[#F5FAFB]/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between"><Brand small /><span className="hidden sm:inline-flex items-center gap-1 text-xs text-[#47606B]"><ShieldCheck size={14} /> Firma protetta e tracciata</span></div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {!error && <div className="max-w-2xl mx-auto"><LinkHero kind="firma" token={token} /></div>}
        {error && <div className="rounded-xl bg-[#FEE2E2] text-[#991B1B] p-6" data-testid="remote-error">{error}</div>}
        {!d && !error && <div className="text-sm text-[#7B929C]">Caricamento…</div>}
        {d && (
          <>
            <div className="fade-up">
              <div className="text-xs uppercase tracking-[0.25em] text-[#7B929C]">Contratto n. {d.number}</div>
              <h1 className="font-serif-it text-4xl sm:text-5xl font-semibold mt-2" data-testid="remote-title">Lettura e firma del contratto</h1>
              <p className="text-sm text-[#47606B] mt-2 max-w-3xl">Gentile {d.data.cliente_nome}, legga con attenzione ogni foglio e apponga la firma nei riquadri indicati. Ogni firma viene registrata con data, ora, indirizzo IP e dispositivo, ed è legata all'impronta digitale del contenuto.</p>
              <a href={`${API}/public/sign/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="remote-full-pdf" className="inline-flex items-center gap-2 mt-4 text-sm px-4 py-2 rounded-full border border-[#D4E6EC] bg-white hover:border-[#0B6E8F]"><FileText size={15} /> Leggi il contratto completo (PDF)</a>
            </div>
            {allDone ? (
              <div className="rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] p-8 space-y-4" data-testid="remote-complete">
                <div className="flex items-center gap-2 text-[#166534] font-serif-it text-3xl font-semibold"><CheckCircle2 /> Grazie, le sue firme sono state registrate</div>
                <p className="text-sm text-[#47606B]">{d.final_available ? "Il contratto è stato firmato da tutte le parti. Può scaricarne copia qui sotto e conservarla." : "Il documento definitivo sarà disponibile non appena tutte le parti avranno firmato. Il Gestore le invierà copia."}</p>
                {d.final_available && <a href={`${API}/public/sign/${token}/final-pdf`} target="_blank" rel="noreferrer" data-testid="remote-final-pdf" className="inline-flex items-center gap-2 text-sm px-5 py-2.5 rounded-full bg-[#0B6E8F] text-white"><Download size={15} /> Scarica copia firmata</a>}
              </div>
            ) : d.otp?.required && !d.otp.verified ? (
              <OtpGate token={token} otp={d.otp} onOk={reload} />
            ) : (
              <SigningFlow pending={d.pending} signatures={d.signatures} data={d.data} extras={d.extras} template={d.template} onSign={sign} />
            )}
          </>
        )}
      </main>
    </div>
  );
}
